<template>
  <ChartBox
    class="generic-wrapper-chart"
    :title="config.title"
    :description="config.description"
  >
    <section
      v-if="config.isLoading"
      class="generic-wrapper-chart__loading"
    >
      <component
        :is="chart.skeleton.name"
        v-if="config.isLoading"
        v-bind="chart.skeleton.props"
      />
    </section>

    <HandleErrorType
      v-if="config.hasError"
      :error-type="config.errorType"
      :on-click="config.onError"
    />

    <EmptyState :show="showEmptyState" />

    <template #title>
      <slot name="title" />
    </template>

    <template #description>
      <slot name="description" />
    </template>

    <template #primary-action>
      <DownloadMenu
        v-if="showDownloadMenu"
        :track-name-on-open="config.downloadMenu.trackNameOnOpen"
        :download-list="downloadList"
        class="generic-wrapper-chart__download-menu"
        @selectDownload="handleDownload"
      />
    </template>

    <template #secondary-action>
      <PeriodSelector
        v-if="showPeriodSelector"
        :period-state-name="config.componentName"
        :default-period-active="periodActive.index"
        @onPeriodChange="handlePeriodChange"
      />

      <GroupSelector
        v-if="showGroupSelector"
        :group-active="groupActive"
        :component-name="config.componentName"
        @onGroupChange="handleGroupChange"
      />
    </template>

    <div v-if="showChart">
      <div class="generic-wrapper-chart__container">
        <div :class="chartClass">
          <component
            :is="chart.name"
            ref="dynamicChart"
            v-observe.once="onObserve"
            data-testid="chart-component"
            v-bind="chart.props"
            :resize="isChartExpanded"
            @legend-click="onChartLegendClick"
          />
        </div>

        <div
          v-if="showInsights"
          class="generic-wrapper-chart__insights"
        >
          <DsInsights
            data-testid="insights-component"
            :class="{'insights__actions--show': showInsightsActions}"
            :insights="config.insights.data"
            @after-leave="toggleChartExpanded"
            @after-enter="toggleChartExpanded"
            @feedback="dispatchTrackOnFeedbackInsight"
            @change-page="dispatchTrackOnChangePageInsight"
          />
        </div>
      </div>

      <slot name="afterChart" />
    </div>
  </ChartBox>
</template>

<script>
import StLoader from '@stilingue/st-loader';
import { DsInsights } from '@ds/components';

import { dispatchMixpanelTrack, isAFunction } from '@/utils';

import { EmptyState, DownloadMenu, PeriodSelector, ChartBox, HandleErrorType, GroupSelector } from '@/components/base';
import { AreaSpline, AreaSplineSkeleton } from '@/components/content/charts/AreaSpline';
import { BasicBar, BasicBarSkeleton } from '@/components/content/charts/BasicBar';
import { DualAxesLineAndColumn, DualAxesLineAndColumnSkeleton } from '@/components/content/charts/DualAxesLineAndColumn';
import { Bubble } from '@/components/content/charts/Bubble';
import { PackedBubble, BubbleSkeleton } from '@/components/content/charts/PackedBubble';
import { AreaSplineAndStackedColumn, AreaSplineAndStackedColumnSkeleton } from '@/components/content/charts/AreaSplineAndStackedColumn';

export default {
  name: 'GenericWrapperChart',
  components: {
    StLoader,
    EmptyState,
    DownloadMenu,
    DsInsights,
    AreaSpline,
    AreaSplineSkeleton,
    BasicBar,
    BasicBarSkeleton,
    DualAxesLineAndColumn,
    PeriodSelector,
    ChartBox,
    Bubble,
    DualAxesLineAndColumnSkeleton,
    HandleErrorType,
    PackedBubble,
    BubbleSkeleton,
    GroupSelector,
    AreaSplineAndStackedColumn,
    AreaSplineAndStackedColumnSkeleton,
  },
  props: {
    config: {
      type: Object,
      required: true,
      validator: (configProp) => {
        const errorMessage = (data) => {
          const hasManyData = data.length > 1;
          const propName = hasManyData ? 'Props' : 'Prop' ;
          const props = hasManyData ? data.join(', ') : data[0];

          return `${propName} required: ${props}`;
        };

        const validateKeys = (keyList) => {
          if (keyList.length > 0) {
            throw new Error(errorMessage(keyList));
          }
        };

        const requiredKeys = {
          default: [
            'componentName',
            'title',
            'description',
            'isLoading',
            'hasError',
            'isEmpty',
            'onError',
            'downloadMenu',
            'chart',
          ],
          downloadMenu: ['trackNameOnOpen', 'trackNameOnDownload'],
          chart: ['type', 'series', 'trackNameOnObserve', 'trackNameOnLegendClick'],
          insights: ['data', 'trackNameToggle', 'trackNameFeedback', 'trackNamePagination'],
          periodSelector: ['trackPeriod'],
          groupSelector: ['trackGroup'],
        };

        const getKeysNotDefined = (keyList, obj) => {
          if (!obj) return keyList.filter(k => !configProp.hasOwnProperty(k));

          return keyList.filter(k => !obj.hasOwnProperty(k));
        };

        const keysToValidate = [
          ...getKeysNotDefined(requiredKeys.default),
          ...getKeysNotDefined(requiredKeys.downloadMenu, configProp.downloadMenu),
          ...getKeysNotDefined(requiredKeys.chart, configProp.chart),
        ];

        if (configProp.insights) {
          validateKeys([
            ...keysToValidate,
            ...getKeysNotDefined(requiredKeys.insights, configProp.insights),
          ]);
        }

        if (configProp.periodSelector) {
          validateKeys([
            ...keysToValidate,
            ...getKeysNotDefined(requiredKeys.periodSelector, configProp.periodSelector),
          ]);
        }

        if (configProp.groupSelector) {
          validateKeys([
            ...keysToValidate,
            ...getKeysNotDefined(requiredKeys.groupSelector, configProp.groupSelector),
          ]);
        }

        validateKeys(keysToValidate);

        return true;
      },
    },
  },
  data() {
    return {
      toggleChart: false,
    };
  },
  computed: {
    chart() {
      const overrideChartOptions = this.config.chart.overrideChartOptions ?? {};
      const categories = this.config.chart.categories ?? [];
      const tooltipConfig = this.config.chart.tooltipConfig;
      const metadata = this.config.chart.metadata ?? {};
      const verticalTextLeft = this.config.chart.verticalTextLeft;
      const verticalTextRight = this.config.chart.verticalTextRight;

      const validateProps = {
        overrideChartOptions,
        series: this.config.chart.series,
      };

      const chartType = {
        AreaSpline: {
          name: 'AreaSpline',
          skeleton: {
            name: 'AreaSplineSkeleton',
            props: {},
          },
          props: {
            ...validateProps,
            ...(tooltipConfig && { tooltipConfig }),
            periodLabel: this.periodActive.label,
          },
        },
        BasicBar: {
          name: 'BasicBar',
          skeleton: {
            name: 'BasicBarSkeleton',
            props: {
              loading: this.config.isLoading,
            },
          },
          props: {
            ...validateProps,
            categories,
          },
        },
        DualAxesLineAndColumn: {
          name: 'DualAxesLineAndColumn',
          skeleton: {
            name: 'DualAxesLineAndColumnSkeleton',
            props: {
              loading: this.config.isLoading,
            },
          },
          props: {
            ...validateProps,
            categories,
          },
        },
        Bubble: {
          name: 'Bubble',
          skeleton: {
            name: 'StLoader',
            props: {
              loading: this.config.isLoading,
            },
          },
          props: {
            ...validateProps,
            axisMetadata: metadata,
          },
        },
        PackedBubble: {
          name: 'PackedBubble',
          skeleton: {
            name: 'BubbleSkeleton',
            props: {},
          },
          props: {
            ...validateProps,
          },
        },
        AreaSplineAndStackedColumn: {
          name: 'AreaSplineAndStackedColumn',
          skeleton: {
            name: 'AreaSplineAndStackedColumnSkeleton',
            props: {},
          },
          props: {
            ...validateProps,
            categories,
            verticalTextLeft,
            verticalTextRight,
          },
        },
      };

      const chartSelected = chartType[this.config.chart.type];

      return {
        name: chartSelected.name,
        props: chartSelected.props,
        skeleton: chartSelected.skeleton,
      };
    },
    showEmptyState() {
      return this.config.isEmpty && !this.config.isLoading;
    },
    showInsights() {
      return this.config?.insights?.data.length > 0;
    },
    showChart() {
      return !this.config.hasError && !this.config.isLoading && !this.config.isEmpty;
    },
    showDownloadMenu() {
      const showDownloadMenuProp = this.config.downloadMenu?.showDownloadMenu ?? true;

      return this.showChart && showDownloadMenuProp;
    },
    isChartExpanded() {
      return this.toggleChart || !this.showInsights;
    },
    chartClass() {
      return {
        'generic-wrapper-chart__chart': this.showInsights,
        'generic-wrapper-chart__chart--close': !this.isChartExpanded,
        'generic-wrapper-chart__chart--full-width': !this.showInsights,
      };
    },
    showPeriodSelector() {
      return this.config.periodSelector?.showPeriodSelector && this.showChart;
    },
    showGroupSelector() {
      return this.config.groupSelector?.showGroupSelector && this.showChart;
    },
    periodActive() {
      if (!this.showPeriodSelector) return {};

      const getterName = `ui/get${this.config.componentName}Period`;

      return {
        index: this.$store.getters[getterName].activeIndex,
        label: this.$store.getters[getterName].label,
      };
    },
    groupActive() {
      if (!this.showGroupSelector) return '';

      const componentName = this.config.componentName.charAt(0).toLowerCase() + this.config.componentName.slice(1);

      return this.$store.state.charts[`${componentName}`].groupSelected;
    },
    showInsightsActions() {
      return this.config?.insights?.data[0].title === '';
    },
    downloadList() {
      return this.config.downloadMenu.downloadList ?? undefined;
    },
  },
  watch: {
    'config.isLoading'(newValue) {
      if (newValue) {
        this.toggleChart = false;
      }
    },
  },
  async mounted() {
    const isNoneRequestRunning = !this.config.isLoading;

    if (isNoneRequestRunning && isAFunction(this.config.requestOnMounted)) {
      this.config.requestOnMounted();
    }
  },
  methods: {
    dispatchTrackOnDownload(type) {
      const trackName = this.config.downloadMenu.trackNameOnDownload;

      dispatchMixpanelTrack(trackName, ['network', 'tab', 'groups'], {
        answer: type,
      });
    },
    async handleDownload({ typeSelected, isDownloadFunction, action }) {
      if (!this.$refs.dynamicChart) return;

      const dynamicChart = this.$refs.dynamicChart.$refs?.chart;

      this.dispatchTrackOnDownload(typeSelected);

      const onSelectDownload = this.config.downloadMenu.onSelectDownload;

      if (isAFunction(onSelectDownload)) {
        onSelectDownload({ typeSelected, isDownloadFunction, action });
        await this.$nextTick();
      }

      if (isDownloadFunction) {
        return dynamicChart?.chart[action]();
      }

      action && dynamicChart?.chart.exportChart({ type: action });
    },
    onChartLegendClick({ legend }) {
      const trackName = this.config.chart.trackNameOnLegendClick;

      dispatchMixpanelTrack(trackName, ['network', 'tab'], {
        answer: legend,
      });
    },
    onObserve() {
      const trackName = this.config.chart.trackNameOnObserve;
      const onObserverFn = this.config.chart.onObserve;

      dispatchMixpanelTrack(trackName, ['network']);

      if (isAFunction(onObserverFn)) {
        onObserverFn();
      }
    },
    dispatchTrackOnToggleInsight() {
      const trackName = this.config.insights.trackNameToggle;

      dispatchMixpanelTrack(trackName, ['network', 'tab'], {
        answer: this.isChartExpanded ? 'close' : 'open',
        chart: this.config.title,
      });
    },
    toggleChartExpanded() {
      this.toggleChart = !this.toggleChart;

      this.dispatchTrackOnToggleInsight();
    },
    dispatchTrackOnFeedbackInsight(payload) {
      const trackName = this.config.insights.trackNameFeedback;

      dispatchMixpanelTrack(trackName, ['network', 'tab'], {
        answer: payload.feedback.value ? 'yes' : 'no',
        chart: this.config.title,
        title: payload.feedback.insight.title,
      });
    },
    dispatchTrackOnChangePageInsight() {
      const trackName = this.config.insights.trackNamePagination;

      dispatchMixpanelTrack(trackName, ['network', 'tab'], {
        chart: this.config.title,
      });
    },
    handlePeriodChange(params) {
      const onPeriodChange = this.config.periodSelector.onPeriodChange;

      if (isAFunction(onPeriodChange)) {
        onPeriodChange(params);
      }

      dispatchMixpanelTrack(this.config.periodSelector.trackPeriod, ['network', 'groups'], {
        answer: params.label,
      });
    },
    async handleGroupChange(params) {
      const onGroupChange = this.config.groupSelector.onGroupChange;

      dispatchMixpanelTrack(this.config.groupSelector.trackGroup, ['network'], {
        answer: params.title,
      });

      if (isAFunction(onGroupChange)) {
        await onGroupChange({ groupSelected: params.id });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.generic-wrapper-chart {
  position: relative;
  border: 1px solid var(--ds-color-silver);
  min-height: 350px;

  &__loading {
    min-height: 350px;
  }

  &__container {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    height: calc(100% - 85px);
    position: relative;
  }

  &__download-menu {
    align-self: flex-start;
  }

  &__chart--close {
    width: calc(100% - 400px) !important;
  }

  &__chart--full-width {
    width: 100% !important;
  }

  &__chart {
    width: calc(100% - 60px);
  }

  &__insights {
    right: 0;
    height: 100%;
    padding-left: 15px;
    position: absolute;
    background-color: var(--ds-color-neutral);
    margin-right: var(--ds-spacing-stack-quark);

    .ds-insights:not(.ds-insights--closed) {
      width: 375px;
    }
  }
}

::v-deep {
  .insights__actions--show {
    div.ds-insights__actions {
      display: none;
    }
  }

  .insights-description {
    &__simple-word {
      font-weight: var(--ds-font-weight-semibold);
    }

    &__neg-number {
      font-weight: var(--ds-font-weight-semibold);
    }

    &__pos-number {
      font-weight: var(--ds-font-weight-semibold);
    }
  }
}
</style>
